import React from 'react';
import { connect } from 'react-redux';
import { Field, FieldArray, reduxForm, formValueSelector } from 'redux-form';
import { map } from 'ramda';
import { Link } from 'react-router-dom';
import ScrollArea from 'react-scrollbar';
import moment from 'moment';

import AddPersonIcon from 'material-ui/svg-icons/social/person-add';
import ImpersonateIcon from 'material-ui/svg-icons/social/group';
import RemoveIcon from 'material-ui/svg-icons/action/delete';
import BackIcon from 'material-ui/svg-icons/navigation/arrow-back';

import { Section, SectionItem } from '../basic/section';
import Button from '../basic/Button';
import ConfirmButton from '../basic/ConfirmButton';
import Toggle from '../basic/Toggle';
import TextField from '../basic/TextField';
import DateField from '../basic/DateField';
import SelectField from '../basic/SelectField';
import AdminOnly from '../../containers/private/AdminOnly';
import TextTag from './TextTagFormField';

import * as Validators from '../../services/Validators';
import { LANGUAGES, CONTRACT_PLAN_TYPES, IDENTIFICATION_DATA_ACCESS, POSTER_TYPES } from '../../constants/Constants';

import './Form.css';

const RESET_PERIODS = [
  {
    value: 'MONTH',
    label: 'Monthly'
  }
];

const minLength8 = Validators.minLength(8);
const minLength6 = Validators.minLength(6);
const minLength3 = Validators.minLength(3);

const renderOperatorFields = ({ fields, impersonateDashboardUser }) => {
  return (
    <div>
      {fields.map((o, idx) => {
        const fld = fields.get(idx);
        const key = fld.id || fld.key;
        const isNew = !fld.id;
        const buttons = [];
        if (!isNew) {
          buttons.push(
            <AdminOnly noProd>
              <Button
                key="operatorImpersonate"
                label="Impersonate"
                onClick={() => impersonateDashboardUser(key)}
                icon={<ImpersonateIcon />}
              />
            </AdminOnly>
          );
        }
        buttons.push(
          <ConfirmButton
            key="operatorRemove"
            label="Remove"
            confirmLabel="Confirm"
            onClick={() => fields.remove(idx)}
            icon={<RemoveIcon />}
          />
        );
        return (
          <SectionItem key={key} contentStyle={{ paddingTop: 0 }} buttons={buttons}>
            <Field name={`${o}.firstName`} component={TextField} label="First name" fullWidth />
            <Field name={`${o}.lastName`} component={TextField} label="Last name" fullWidth />
            <SectionItem
              sensitiveEdit={!isNew}
              style={{ border: 0 }}
              contentStyle={{ padding: 0 }}
              overlayStyle={{ left: '-16px', right: '-16px' }}
            >
              {({ editAllowed }) => (
                <Field
                  name={`${o}.email`}
                  component={TextField}
                  label="Email"
                  disabled={!editAllowed}
                  fullWidth
                  validate={[Validators.required, Validators.email, minLength6]}
                />
              )}
            </SectionItem>
            <br />
            <Field name={`${o}.active`} component={Toggle} label="Active" />
            <Field
              name={`${o}.language`}
              component={SelectField}
              label="Language"
              items={LANGUAGES}
              fullWidth
              validate={Validators.required}
            />
            {isNew && (
              <div>
                <Field
                  name={`${o}.password`}
                  component={TextField}
                  label="Password"
                  fullWidth
                  type="password"
                  validate={[Validators.required, minLength8]}
                />
                <Field
                  name={`${o}.repeatPassword`}
                  component={TextField}
                  label="Repeat password"
                  fullWidth
                  type="password"
                  validate={[Validators.required, minLength8]}
                />
                <br />
              </div>
            )}
            <Field name={`${o}.flagPositionCreation`} component={Toggle} label="Can create positions" fullWidth />
            <br />
            <Field name={`${o}.flagProjectDataExport`} component={Toggle} label="Can export project data" fullWidth />
            <br />
            <Field name={`${o}.flagDashboardChart`} component={Toggle} label="Can use dashboard chart" fullWidth />
            <br />
            <Field name={`${o}.flagWeights`} component={Toggle} label="Can use competency weights" fullWidth />
            <br />
            <Field name={`${o}.flagEmailInvitation`} component={Toggle} label="Can use email invitation" fullWidth />
            {(fld.activatedAt || fld.createdAt) && (
              <div style={{ padding: '8px', marginTop: '16px', borderRadius: '2px', background: 'whitesmoke' }}>
                {fld.createdAt && (
                  <div className="Text-regular">
                    <span className="floating-label">Created at</span>
                    <div>
                      <span>{moment(fld.createdAt).format('L LT')}</span>
                    </div>
                  </div>
                )}
                {fld.activatedAt && (
                  <div className="Text-regular">
                    <span className="floating-label">Activated at</span>
                    <div>
                      <span>{moment(fld.activatedAt).format('L LT')}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </SectionItem>
        );
      })}
      <SectionItem
        buttons={[
          <Button
            key="operatorAdd"
            label="Add operator"
            onClick={() =>
              fields.push({
                key: Math.random().toString(36).substring(2),
                active: true
              })
            }
            primary
            icon={<AddPersonIcon />}
          />
        ]}
      />
    </div>
  );
};

const CompanyForm = props => {
  const {
    profileLimitReset,
    freeContractPlan,
    saveInProgress,
    initialValues,
    accountGroups,
    profileLimitExpirationEnabled,
    onDelete
  } = props;
  const { handleSubmit, impersonateDashboardUser } = props;
  const isNew = !initialValues.id;
  return (
    <form className="Form" onSubmit={handleSubmit}>
      <ScrollArea smoothScrolling>
        <div className="Form-content-container">
          <div className="container">
            <div className="row">
              <div className="col-md-6">
                <Section title="General info">
                  <SectionItem contentStyle={{ paddingTop: 0 }}>
                    <Field
                      name="name"
                      component={TextField}
                      label="Name"
                      fullWidth
                      validate={[Validators.required, minLength3]}
                    />
                  </SectionItem>
                  <SectionItem contentStyle={{ paddingTop: 0 }} sensitiveEdit={!isNew}>
                    {({ editAllowed }) => (
                      <Field
                        name="slug"
                        component={TextField}
                        label="Slug"
                        fullWidth
                        validate={[Validators.required, minLength3]}
                        disabled={!isNew && !editAllowed}
                      />
                    )}
                  </SectionItem>
                  <SectionItem contentStyle={{ paddingTop: 0 }}>
                    <Field
                      name="primaryLanguage"
                      component={SelectField}
                      label="Primary language"
                      items={LANGUAGES}
                      fullWidth
                      validate={Validators.required}
                    />
                  </SectionItem>
                  <AdminOnly>
                    <SectionItem contentStyle={{ paddingTop: 0 }}>
                      <Field
                        name="accountGroup"
                        component={SelectField}
                        label="Account group"
                        fullWidth
                        items={accountGroups}
                        withEmptyItem
                      />
                    </SectionItem>
                  </AdminOnly>
                  <SectionItem>
                    <Field
                      name="interpositionSim"
                      component={Toggle}
                      label="Interposition simulation"
                      fullWidth
                      helperText="The applicant's simulation / profile transferable between multiple positions within this account"
                    />
                  </SectionItem>
                  <SectionItem>
                    <Field
                      name="noPasswordFlow"
                      component={Toggle}
                      label="No password flow"
                      fullWidth
                      helperText="The registration flow without having to choose a password"
                    />
                  </SectionItem>
                  {initialValues.createdAt && (
                    <SectionItem>
                      <div style={{ padding: '8px', borderRadius: '2px', background: 'whitesmoke' }}>
                        <div className="Text-regular">
                          <span className="floating-label">Created at</span>
                          <div>
                            <span>{moment(initialValues.createdAt).format('L LT')}</span>
                          </div>
                        </div>
                      </div>
                    </SectionItem>
                  )}
                </Section>
                <Section title="Modes">
                  <SectionItem>
                    <Field
                      name="isVendor"
                      component={Toggle}
                      label="Vendor"
                      helperText="Vendor accounts use primarily advertising and no HR functionality"
                      fullWidth
                    />
                  </SectionItem>
                  <SectionItem>
                    <Field
                      name="modeSourcing"
                      component={Toggle}
                      label="Sourcing"
                      helperText="Allow advertising in Talent Opportunity Marketplace with company profile"
                      fullWidth
                      containerStyle={{ marginBottom: '8px' }}
                    />
                    <Field
                      name="modeRecruitment"
                      component={Toggle}
                      label="Recruitment"
                      helperText="Allow using job position and talent academy projects"
                      fullWidth
                      containerStyle={{ marginBottom: '8px' }}
                    />
                    <Field
                      name="modeDevelopment"
                      component={Toggle}
                      label="Development"
                      helperText="Allow using team assessment projects"
                      fullWidth
                      containerStyle={{ marginBottom: '8px' }}
                    />
                    <Field name="modeTeams" component={Toggle} label="Teams" helperText="Allow using teams" fullWidth />
                  </SectionItem>
                </Section>
                <Section title="Operators">
                  <SectionItem contentStyle={{ paddingTop: 0 }}>
                    <Field
                      name="operatorLimit"
                      component={TextField}
                      label="Maximum number of operators allowed"
                      fullWidth
                      helperText="Set to 0 to disable the limit"
                    />
                  </SectionItem>
                  <FieldArray
                    name="operators"
                    component={renderOperatorFields}
                    impersonateDashboardUser={impersonateDashboardUser}
                  />
                </Section>
              </div>

              <div className="col-md-6">
                <Section title="Initiative types">
                  <SectionItem>
                    <Field
                      name="initiativeTypesWBI"
                      component={Toggle}
                      label="Well-being Index"
                      helperText="Allow creating the 'Well-being Index' initiative"
                      fullWidth
                      containerStyle={{ marginBottom: '8px' }}
                    />
                    <Field
                      name="initiativeTypesCustom"
                      component={Toggle}
                      label="Custom"
                      helperText="Allow creating the 'Custom' initiative"
                      fullWidth
                      containerStyle={{ marginBottom: '8px' }}
                    />
                    <Field
                      name="initiativeTypesEngagementIndex"
                      component={Toggle}
                      label="Engagement Index"
                      helperText="Allow creating the 'Engagement Index' initiative"
                      fullWidth
                    />
                  </SectionItem>
                </Section>

                <Section title="Credits">
                  <SectionItem contentStyle={{ paddingTop: 0 }}>
                    <Field
                      name="profileLimitNumber"
                      type="number"
                      component={TextField}
                      label="Number of credits"
                      helperText="Set to 0 to disable the limit"
                      fullWidth
                    />
                  </SectionItem>
                  <SectionItem>
                    <Field
                      name="profileLimitExpirationEnabled"
                      component={Toggle}
                      label="Expiration enabled"
                      fullWidth
                    />
                    <Field
                      name="profileLimitExpiration"
                      component={DateField}
                      label="Expiration date"
                      fullWidth
                      disabled={!profileLimitExpirationEnabled}
                    />
                  </SectionItem>
                  <AdminOnly>
                    <SectionItem>
                      <Field
                        name="profileLimitPerPosition"
                        component={Toggle}
                        label="Limit applies per position"
                        fullWidth
                      />
                    </SectionItem>
                  </AdminOnly>
                  <AdminOnly>
                    <SectionItem>
                      <Field name="profileLimitReset" component={Toggle} label="Limit resets" fullWidth />
                      <Field
                        name="profileLimitPeriod"
                        component={SelectField}
                        label="Reset period"
                        fullWidth
                        items={RESET_PERIODS}
                        disabled={!profileLimitReset}
                      />
                    </SectionItem>
                  </AdminOnly>
                </Section>

                <Section title="Admin access">
                  <SectionItem contentStyle={{ paddingTop: 0 }}>
                    <Field
                      name="adminAccessParticipantData"
                      component={SelectField}
                      label="Participant data"
                      items={IDENTIFICATION_DATA_ACCESS}
                      fullWidth
                      withEmptyItem
                    />
                  </SectionItem>
                </Section>

                <Section title="Contract plan type">
                  <SectionItem contentStyle={{ paddingTop: 0 }}>
                    <Field
                      name="contractPlanType"
                      component={SelectField}
                      label="Type"
                      items={CONTRACT_PLAN_TYPES}
                      fullWidth
                    />
                  </SectionItem>
                </Section>

                <Section title="Enabled performance indicators">
                  <SectionItem>
                    <Field
                      name="perfIndicatorIndependence"
                      component={Toggle}
                      label="Independence"
                      fullWidth
                      disabled={!freeContractPlan}
                    />
                  </SectionItem>
                  <SectionItem>
                    <Field
                      name="perfIndicatorSelfStylization"
                      component={Toggle}
                      label="Self-stylization"
                      fullWidth
                      disabled={!freeContractPlan}
                    />
                  </SectionItem>
                </Section>

                <Section title="Enabled features">
                  <SectionItem>
                    <Field
                      name="featureSkillsDescription"
                      component={Toggle}
                      label="Skills description"
                      fullWidth
                      disabled={!freeContractPlan}
                    />
                  </SectionItem>
                  <SectionItem>
                    <Field
                      name="featureRecommendations"
                      component={Toggle}
                      label="Recommendations"
                      fullWidth
                      disabled={!freeContractPlan}
                    />
                  </SectionItem>
                  <SectionItem>
                    <Field
                      name="featureProfilePrint"
                      component={Toggle}
                      label="Profile print"
                      fullWidth
                      disabled={!freeContractPlan}
                    />
                  </SectionItem>
                </Section>

                <Section title="Miscellaneous">
                  <SectionItem>
                    <Field
                      name="teaseDashboardChart"
                      component={Toggle}
                      label="Tease dashboard chart"
                      fullWidth
                      disabled={!freeContractPlan}
                    />
                  </SectionItem>
                  <SectionItem>
                    <Field name="eapiEnabled" component={Toggle} label="EAPI enabled" fullWidth />
                  </SectionItem>
                  <SectionItem>
                    <Field
                      name="internalTags"
                      component={TextTag}
                      hintText="Add a tag..."
                      helperText="Internal tags serve to group companies for analytical purposes"
                      label="Internal tags"
                      fullWidth
                    />
                  </SectionItem>

                  <SectionItem>
                    <Field
                      name="echoPulsePromptContextEN"
                      component={TextField}
                      multiLine
                      rows={3}
                      label="Echo Pulse prompt context"
                      helperText="Custom company-wide prompt context for Echo Pulse"
                      fullWidth
                    />
                  </SectionItem>

                  <SectionItem>
                    <Field
                      name="posterType"
                      component={SelectField}
                      label="Culture Fit poster type"
                      items={POSTER_TYPES}
                      fullWidth
                    />
                  </SectionItem>
                </Section>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>
      <div className="Form-buttons-container">
        <div className="Form-buttons clearfix">
          <div className="float-left" style={{ fontSize: 0 }}>
            <Button
              label="Back"
              icon={<BackIcon />}
              style={{ fontSize: '16px' }}
              containerElement={<Link to="/settings/companies" />}
            />
          </div>
          <div className="float-right">
            {!isNew && (
              <Button
                raised
                secondary
                label="Delete"
                loading={saveInProgress}
                onClick={onDelete}
                style={{ marginRight: '8px' }}
              />
            )}
            <Button raised primary label={isNew ? 'Create' : 'Save'} type="submit" loading={saveInProgress} />
          </div>
        </div>
      </div>
    </form>
  );
};

const featureEnabled = (plan, feature) =>
  !(plan && plan.disabledFeatures && plan.disabledFeatures.indexOf(feature) > -1);

const hasContractPlanMode = (plan, mode) => plan && plan.modes && plan.modes.indexOf(mode) > -1;

const hasEnabledInitiativeType = (plan, type) =>
  !plan || !plan.disabledInitiativeTypes || plan.disabledInitiativeTypes.indexOf(type) === -1;

const selector = formValueSelector('company');
const mapStateToProps = state => {
  const {
    entities: {
      companies: { currentCompany = {}, currentCompanySaving },
      accountGroups
    }
  } = state;
  const { name, slug, contractPlan, features, data } = currentCompany;
  const contractPlanType = selector(state, 'contractPlanType');
  const isNew = !currentCompany.id;
  return {
    saveInProgress: currentCompanySaving,
    initialValues: {
      ...currentCompany,
      name,
      slug,
      interpositionSim: isNew ? true : features && features.indexOf('INTERPOSITION_SIM') > -1,
      noPasswordFlow: features && features.indexOf('NO_PASSWORD_FLOW') > -1,
      operators: map(
        o => ({
          ...o,
          flagPositionCreation: o.flags && o.flags.indexOf('POSITION_CREATION') > -1,
          flagProjectDataExport: o.flags && o.flags.indexOf('PROJECT_DATA_EXPORT') > -1,
          flagDashboardChart: o.flags && o.flags.indexOf('DASHBOARD_CHART') > -1,
          flagWeights: o.flags && o.flags.indexOf('WEIGHTS') > -1,
          flagEmailInvitation: o.flags && o.flags.indexOf('EMAIL_INVITATION') > -1
        }),
        currentCompany.operators || []
      ),
      // contract plan
      profileLimitNumber: contractPlan && contractPlan.profileLimit && contractPlan.profileLimit.number,
      profileLimitReset: contractPlan && contractPlan.profileLimit && !!contractPlan.profileLimit.resetPeriod,
      profileLimitPeriod: contractPlan && contractPlan.profileLimit && contractPlan.profileLimit.resetPeriod,
      profileLimitExpirationEnabled:
        contractPlan && contractPlan.profileLimit && !!contractPlan.profileLimit.expiration,
      profileLimitExpiration:
        contractPlan &&
        contractPlan.profileLimit &&
        contractPlan.profileLimit.expiration &&
        new Date(contractPlan.profileLimit.expiration),
      profileLimitPerPosition: contractPlan && contractPlan.profileLimit && !contractPlan.profileLimit.global,
      perfIndicatorIndependence: !(
        contractPlan &&
        contractPlan.disabledPerformanceIndicators &&
        contractPlan.disabledPerformanceIndicators.indexOf('independence') > -1
      ),
      perfIndicatorSelfStylization: !(
        contractPlan &&
        contractPlan.disabledPerformanceIndicators &&
        contractPlan.disabledPerformanceIndicators.indexOf('selfStylization') > -1
      ),
      featureSkillsDescription: featureEnabled(contractPlan, 'SKILLS_DESCRIPTION'),
      featureRecommendations: featureEnabled(contractPlan, 'RECOMMENDATIONS'),
      featureProfilePrint: featureEnabled(contractPlan, 'PROFILE_PRINT'),
      operatorLimit: contractPlan && contractPlan.operatorLimit,
      contractPlanType: isNew ? 'PILOT' : contractPlan && contractPlan.type,
      teaseDashboardChart: contractPlan && contractPlan.teaseDashboardChart,
      eapiEnabled: contractPlan && contractPlan.eapiEnabled,
      accountGroup: currentCompany.accountGroup && currentCompany.accountGroup.id,
      primaryLanguage: currentCompany.primaryLanguage,
      modeSourcing: hasContractPlanMode(contractPlan, 'SOURCING'),
      modeRecruitment: hasContractPlanMode(contractPlan, 'RECRUITMENT'),
      modeDevelopment: hasContractPlanMode(contractPlan, 'DEVELOPMENT'),
      modeTeams: isNew ? true : hasContractPlanMode(contractPlan, 'TEAMS'),
      isVendor: contractPlan && contractPlan.vendor,
      adminAccessParticipantData: isNew ? 'FULL' : data && data.participantAccess && data.participantAccess.admin,
      posterType: data && data.posterType,
      internalTags: data && data.internalTags,
      initiativeTypesWBI: isNew ? false : hasEnabledInitiativeType(contractPlan, 'WELL_BEING_INDEX'),
      initiativeTypesCustom: isNew ? false : hasEnabledInitiativeType(contractPlan, 'CUSTOM'),
      initiativeTypesEngagementIndex: isNew ? true : hasEnabledInitiativeType(contractPlan, 'ENGAGEMENT_INDEX'),
      echoPulsePromptContextEN: data && data.promptContext && data.promptContext.en && data.promptContext.en.echoPulse
    },
    profileLimitReset: selector(state, 'profileLimitReset'),
    profileLimitExpirationEnabled: selector(state, 'profileLimitExpirationEnabled'),
    freeContractPlan: contractPlanType === 'FREE' || contractPlanType === 'LIMITED',
    accountGroups: map(({ name, id }) => ({ label: name, value: id }), accountGroups.items)
  };
};

export default connect(mapStateToProps)(
  reduxForm({
    form: 'company',
    enableReinitialize: true,
    validate: values => {
      const errors = {};
      if (values.operators) {
        const opErrors = [];
        values.operators.forEach((o, idx) => {
          if (!o.id && o.password !== o.repeatPassword) {
            opErrors[idx] = {
              repeatPassword: 'Passwords must match'
            };
          }
        });
        if (opErrors.length) {
          errors.operators = opErrors;
        }
      }
      return errors;
    }
  })(CompanyForm)
);



// WEBPACK FOOTER //
// ./src/components/forms/CompanyForm.js